import { useAppSelector } from '@dealstack/frontend/app/hooks/redux';
import z from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import { ProgrammeUserRow } from '@dealstack/frontend/app/pages/Project/MIP/MIPCreate/Collaborate/Collaborate.types';
import {
	useTranslationCommonForm,
	useTranslationMipCreate,
} from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks';
import { PermissionRoleWithOwnerEnum } from '@dealstack/shared/lib/generated';
import {
	getResourceTypePermissionsWithOwner,
	ResourceType,
} from '@dealstack/shared/lib/common/fineGrainedAuthorization/permissions';
import { useEffect, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { TEST_ELEMENT_IDS } from '@dealstack/frontend/shared/helper/UIElementIdentifier/UIElementIdentifier';
import { Option, Select } from '@dealstack/frontend/shared/components/forms/Select/Select';
import { StringInput } from '@dealstack/frontend/shared/components/forms/StringInput/StringInput';
import {
	CloseButtonSquare,
	DeleteButtonSquare,
	SaveButtonSquare,
	ThreeDotMenuIconButton,
} from '@dealstack/frontend/shared/components/fundament/Button/CommonButtons';
import { Checkbox } from '@dealstack/frontend/shared/components/fundament/Checkbox/Checkbox';
import { Text } from '@dealstack/frontend/shared/components/fundament/Text/Text';
import { Cell } from '@dealstack/frontend/shared/components/Table/DefaultTable/Cell';
import { Row } from '@dealstack/frontend/shared/components/Table/DefaultTable/Row';
import styled, { css } from 'styled-components';
import { createFieldNames } from '@dealstack/shared/lib/common/zod/createFieldName';
import { Flex } from '@mantine/core';

interface Props {
	user: ProgrammeUserRow;
	index: number;
	onUpdateRow: (updatedProgrammeUserData: ProgrammeUserRow, rowIndex: number) => void;
}

export const CollaboratorRow = ({ user, index, onUpdateRow }: Props) => {
	const tMipCreate = useTranslationMipCreate();
	const userState = useAppSelector((state) => state.user).data;
	const tCommonForm = useTranslationCommonForm();

	const [isEdit, setIsEdit] = useState(false);
	const { isContactPerson, userProgrammeId } = user;
	const userRole = user.permissionRoles[0];
	useEffect(() => {
		if (!userProgrammeId) {
			setIsEdit(true);
		}
	}, []);

	const schema = z.object({
		firstName: z.string().trim().min(1, tCommonForm('field_required')),
		lastName: z.string().trim().min(1, tCommonForm('field_required')),
		email: z
			.string()
			.trim()
			.email(tCommonForm('invalid_email'))
			.min(1, tCommonForm('field_required')),
		role: z.nativeEnum(PermissionRoleWithOwnerEnum),
		isContactPerson: z.boolean(),
	});
	const fieldNames = createFieldNames(schema);

	type FormData = z.infer<typeof schema>;

	const programmeUserRowForm = useForm({
		mode: 'all',
		reValidateMode: 'onChange',
		defaultValues: {
			firstName: user.firstName,
			lastName: user.lastName,
			email: user.email,
			role: userRole,
			isContactPerson,
		},
		resolver: zodResolver(schema),
	});

	useEffect(() => {
		programmeUserRowForm.reset({
			firstName: user.firstName,
			lastName: user.lastName,
			email: user.email,
			role: userRole,
			isContactPerson: user.isContactPerson,
		});
	}, [user]);

	const onSubmit = (data: FormData) => {
		const dirtyFields = programmeUserRowForm.formState.dirtyFields;

		// Cannot use the isDirty flag from the formState as for new users is false
		if (Object.entries(dirtyFields).length === 0) {
			setIsEdit(false);
			return;
		}

		const hasUpdatedData = (dirtyFields.firstName || dirtyFields.lastName) ?? undefined;

		const hasUpdatedPermissions = dirtyFields.role ?? undefined;

		const updatedProgrammeUser: ProgrammeUserRow = {
			userProgrammeId,
			id: user.id,
			firstName: data.firstName,
			lastName: data.lastName,
			email: data.email,
			active: user.active,
			permissionRoles: [data.role],
			isContactPerson: data.isContactPerson,
			hasUpdatedData,
			hasUpdatedPermissions,
			deleted: false,
			canBeRemoved: true,
		};

		onUpdateRow(updatedProgrammeUser, index);
		setIsEdit(false);
	};

	const onCancel = () => {
		if (!userProgrammeId) {
			onDelete();
			return;
		}

		programmeUserRowForm.reset();
		setIsEdit(false);
	};

	const onDelete = () => {
		onUpdateRow({ ...user, isDeleted: true }, index);
	};

	const programmePermissions = getResourceTypePermissionsWithOwner(ResourceType.PROGRAMME);
	const programmesRoles = programmePermissions.map((permission) => ({
		label: tMipCreate(`invite_team_member.relation.${permission}`),
		value: permission,
	}));

	const permissionValue = user.permissionRoles[0];
	const permissionLabel = programmesRoles.find((el) => el.value === permissionValue)?.label;
	const show3DotMenu = userState?.id === user.id;

	return (
		<StyledRow
			isRowInvalid={
				!!programmeUserRowForm.formState.errors.email ||
				!!programmeUserRowForm.formState.errors.firstName ||
				!!programmeUserRowForm.formState.errors.lastName
			}
		>
			<FormProvider {...programmeUserRowForm}>
				<Cell>
					<Text variant="p-sm" bold>
						{index + 1}.
					</Text>
				</Cell>

				{/* Conditionally Render Either Edit Mode or Read-Only Mode */}
				{isEdit ? (
					<>
						<Cell>
							<StringInput
								data-testid={TEST_ELEMENT_IDS.MIP_CREATE_COLLABORATE_NAME_INPUT}
								name={fieldNames.firstName}
								maxSize
								disabled={user.active}
								placeholder="First name"
							/>
						</Cell>
						<Cell>
							<StringInput
								data-testid={TEST_ELEMENT_IDS.MIP_CREATE_COLLABORATE_NAME_INPUT}
								name={fieldNames.lastName}
								maxSize
								disabled={user.active}
								placeholder="Last name"
							/>
						</Cell>
						<Cell>
							<StringInput
								data-testid={TEST_ELEMENT_IDS.MIP_CREATE_COLLABORATE_MAIL_INPUT}
								name={fieldNames.email}
								maxSize
								disabled={Boolean(userProgrammeId)}
								placeholder="email"
							/>
						</Cell>
						<Cell>
							<RightsSelect
								data-testid={
									TEST_ELEMENT_IDS.MIP_CREATE_COLLABORATE_COLLABORATOR_ROW
								}
								programmesRoles={programmesRoles}
							/>
						</Cell>
						<Cell>
							<StyledCheckbox
								data-testid={
									TEST_ELEMENT_IDS.MIP_CREATE_COLLABORATE_IS_INVESTOR_CONTACT
								}
								onClick={() => {
									programmeUserRowForm.setValue(
										'isContactPerson',
										!programmeUserRowForm.watch('isContactPerson'),
										{ shouldValidate: true, shouldDirty: true }
									);
								}}
								checked={programmeUserRowForm.watch('isContactPerson')}
								label={tMipCreate('collaborate.table.is_contact_person')}
							/>
						</Cell>
						<Cell align="right">
							<Flex gap="0.25rem">
								<SaveButtonSquare
									data-testid={
										TEST_ELEMENT_IDS.MIP_CREATE_COLLABORATE_SAVE_ROW_BUTTON
									}
									onClick={programmeUserRowForm.handleSubmit(onSubmit)}
								/>
								<CloseButtonSquare
									data-testid={
										TEST_ELEMENT_IDS.MIP_CREATE_COLLABORATE_CLOSE_ROW_BUTTON
									}
									onClick={onCancel}
								/>
								<DeleteButtonSquare
									data-testid={
										TEST_ELEMENT_IDS.MIP_CREATE_COLLABORATE_DELETE_ROW_BUTTON
									}
									type="button"
									onClick={onDelete}
								/>
							</Flex>
						</Cell>
					</>
				) : (
					<>
						<Cell>
							<Text variant="p-sm">{user.firstName}</Text>
						</Cell>
						<Cell>
							<Text variant="p-sm">{user.lastName}</Text>
						</Cell>
						<Cell>
							<Text variant="p-sm" alwaysWrap>
								{user.email}
							</Text>
						</Cell>
						<Cell>
							<Text variant="p-sm">{permissionLabel}</Text>
						</Cell>
						<Cell>
							<StyledCheckbox
								data-testid={
									TEST_ELEMENT_IDS.MIP_CREATE_COLLABORATE_IS_INVESTOR_CONTACT
								}
								onClick={() => {
									setIsEdit(true);
									programmeUserRowForm.setValue(
										'isContactPerson',
										!programmeUserRowForm.watch('isContactPerson'),
										{ shouldValidate: true, shouldDirty: true }
									);
								}}
								checked={programmeUserRowForm.watch('isContactPerson')}
								label={tMipCreate('collaborate.table.is_contact_person')}
							/>
						</Cell>
						<Cell align="right">
							{!show3DotMenu && (
								<ThreeDotMenuIconButton
									data-testid={
										TEST_ELEMENT_IDS.MIP_CREATE_COLLABORATE_THREE_DOT_MENU
									}
									onClick={() => setIsEdit(true)}
								/>
							)}
						</Cell>
					</>
				)}
			</FormProvider>
		</StyledRow>
	);
};
const RightsSelect = styled(({ programmesRoles }) => {
	return (
		<Select name="role" maxSize>
			{programmesRoles.map((role) => (
				<Option value={role.value}>{role.label}</Option>
			))}
		</Select>
	);
})``;

const StyledCheckbox = styled(Checkbox)`
	width: 85%;
	background-color: ${({ theme }) => theme.v2.color.white[500]};
	border: 1px solid ${({ theme }) => theme.v2.color.neutral[500]};
`;

const StyledRow = styled(Row)<{ isRowInvalid?: boolean }>`
	${({ isRowInvalid }) =>
		isRowInvalid &&
		css`
			align-items: flex-start;
			display: flex;
			flex-direction: row;

			${Cell}:first-child {
				margin-top: 0.6em;
			}
		`}
`;
